import { useCallback, useEffect, useRef, useState } from 'react';

export type AutoRefreshContext = {
  background: boolean;
};

export type AutoRefreshResumeReason = 'visible' | 'focus' | 'online';

export interface AutoRefreshOptions {
  enabled?: boolean;
  intervalMs: number;
  hiddenIntervalMs?: number;
  onRefresh: (context: AutoRefreshContext) => Promise<void> | void;
  refreshOnFocus?: boolean;
  pauseWhenHidden?: boolean;
  pauseWhenOffline?: boolean;
  getIntervalMs?: () => number;
  deps?: readonly unknown[];
  storageKey?: string;
  onResume?: (reason: AutoRefreshResumeReason) => { background: boolean } | void;
}

export interface AutoRefreshControl {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  refreshing: boolean;
  lastUpdatedAt: Date | null;
  error: string | null;
  refresh: () => Promise<void>;
  intervalMs: number;
  setIntervalMs: (ms: number) => void;
}

const RESUME_DEBOUNCE_MS = 300;

function readStoredToggle(storageKey: string | undefined): boolean {
  if (!storageKey) return true;
  const stored = sessionStorage.getItem(storageKey);
  if (stored === null) return true;
  return stored === 'true';
}

export function useAutoRefresh(options: AutoRefreshOptions): AutoRefreshControl {
  const {
    enabled: enabledOption = true,
    intervalMs: initialIntervalMs,
    hiddenIntervalMs,
    onRefresh,
    refreshOnFocus = true,
    pauseWhenHidden = true,
    pauseWhenOffline = true,
    getIntervalMs,
    deps = [],
    storageKey,
    onResume,
  } = options;

  const [userEnabled, setUserEnabled] = useState(() => readStoredToggle(storageKey));
  const [intervalMs, setIntervalMsState] = useState(initialIntervalMs);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enabled = enabledOption && userEnabled;

  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const onResumeRef = useRef(onResume);
  onResumeRef.current = onResume;
  const userEnabledRef = useRef(userEnabled);
  userEnabledRef.current = userEnabled;
  const enabledOptionRef = useRef(enabledOption);
  enabledOptionRef.current = enabledOption;

  const isPollingEnabled = useCallback(
    () => enabledOptionRef.current && userEnabledRef.current,
    []
  );

  const refreshingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generationRef = useRef(0);
  const mountedRef = useRef(true);
  const lastResumeAtRef = useRef(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const resolveDelay = useCallback((): number | null => {
    if (pauseWhenOffline && typeof navigator !== 'undefined' && !navigator.onLine) {
      return null;
    }
    const visible = document.visibilityState === 'visible';
    if (pauseWhenHidden && !visible) {
      return null;
    }
    if (getIntervalMs) {
      return getIntervalMs();
    }
    if (!visible && hiddenIntervalMs != null) {
      return hiddenIntervalMs;
    }
    return intervalMs;
  }, [getIntervalMs, hiddenIntervalMs, intervalMs, pauseWhenHidden, pauseWhenOffline]);

  const runRefresh = useCallback(async (background: boolean): Promise<void> => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    if (mountedRef.current && background) {
      setRefreshing(true);
    }
    try {
      await Promise.resolve(onRefreshRef.current({ background }));
      if (!mountedRef.current) return;
      setLastUpdatedAt(new Date());
      setError(null);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e instanceof Error ? e.message : 'Refresh failed');
    } finally {
      refreshingRef.current = false;
      if (mountedRef.current) {
        setRefreshing(false);
      }
    }
  }, []);

  const scheduleNext = useCallback(
    (generation: number) => {
      clearTimer();
      if (!isPollingEnabled() || generation !== generationRef.current) return;
      const delay = resolveDelay();
      if (delay == null) return;
      timerRef.current = setTimeout(() => {
        if (!mountedRef.current || generation !== generationRef.current) return;
        void (async () => {
          await runRefresh(true);
          if (!mountedRef.current || generation !== generationRef.current) return;
          scheduleNext(generation);
        })();
      }, delay);
    },
    [clearTimer, isPollingEnabled, resolveDelay, runRefresh]
  );

  const refresh = useCallback(async () => {
    if (refreshingRef.current) return;
    clearTimer();
    const generation = generationRef.current;
    await runRefresh(false);
    if (mountedRef.current && generation === generationRef.current && isPollingEnabled()) {
      scheduleNext(generation);
    }
  }, [clearTimer, isPollingEnabled, runRefresh, scheduleNext]);

  const resumeRefresh = useCallback(
    async (reason: AutoRefreshResumeReason) => {
      if (!isPollingEnabled()) return;
      const now = Date.now();
      if (now - lastResumeAtRef.current < RESUME_DEBOUNCE_MS) return;
      lastResumeAtRef.current = now;

      clearTimer();
      const generation = generationRef.current;
      const resumeOpts = onResumeRef.current?.(reason);
      const background = resumeOpts?.background ?? true;
      await runRefresh(background);
      if (mountedRef.current && generation === generationRef.current) {
        scheduleNext(generation);
      }
    },
    [clearTimer, isPollingEnabled, runRefresh, scheduleNext]
  );

  useEffect(() => {
    if (!enabledOption) {
      return undefined;
    }

    mountedRef.current = true;
    generationRef.current += 1;
    const generation = generationRef.current;

    void (async () => {
      await runRefresh(false);
      if (mountedRef.current && generation === generationRef.current && isPollingEnabled()) {
        scheduleNext(generation);
      }
    })();

    const onVisibilityChange = () => {
      if (generation !== generationRef.current) return;
      clearTimer();
      if (document.visibilityState === 'visible') {
        void resumeRefresh('visible');
      } else if (!pauseWhenHidden) {
        scheduleNext(generation);
      }
    };

    const onFocus = () => {
      if (!refreshOnFocus || generation !== generationRef.current) return;
      if (document.visibilityState !== 'visible') return;
      void resumeRefresh('focus');
    };

    const onOnline = () => {
      if (generation !== generationRef.current) return;
      void resumeRefresh('online');
    };

    const onOffline = () => {
      if (generation !== generationRef.current) return;
      clearTimer();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      refreshingRef.current = false;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are caller-controlled refresh triggers
  }, [enabledOption, ...deps]);

  useEffect(() => {
    if (!enabledOption) return;
    const generation = generationRef.current;
    clearTimer();
    if (userEnabled) {
      scheduleNext(generation);
    }
  }, [clearTimer, enabledOption, intervalMs, scheduleNext, userEnabled]);

  const setEnabled = useCallback(
    (value: boolean) => {
      setUserEnabled(value);
      if (storageKey) {
        sessionStorage.setItem(storageKey, String(value));
      }
    },
    [storageKey]
  );

  const setIntervalMs = useCallback((ms: number) => {
    setIntervalMsState(ms);
  }, []);

  return {
    enabled,
    setEnabled,
    refreshing,
    lastUpdatedAt,
    error,
    refresh,
    intervalMs,
    setIntervalMs,
  };
}
